import React, { useState, useEffect } from 'react';
import { X, CreditCard, User, Calendar, Upload, FileText } from 'lucide-react';
import { Payment } from '../../types/collaborators';
import { collaboratorInvoicesService } from '../../services/supabaseService';
import { formatCurrency, currencySymbol } from '../../utils/textUtils';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payment: Partial<Payment>) => void;
  payment?: Payment | null;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onSave, payment }) => {
  const [formData, setFormData] = useState({
    invoiceId: '',
    invoiceNumber: '',
    collaboratorId: '',
    collaboratorName: '',
    paymentDate: '',
    amount: 0,
    currency: 'EUR',
    paymentMethod: '',
    proofUrl: '',
    proofName: '',
    status: 'scheduled' as Payment['status'],
    notes: ''
  });

  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const paymentMethods = [
    'Transferencia Bancaria',
    'PayPal',
    'Wise',
    'Stripe',
    'Efectivo',
    'Cheque',
    'Otro'
  ];

  useEffect(() => {
    if (isOpen) {
      loadInvoicesData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (payment) {
      setFormData({
        invoiceId: payment.invoiceId,
        invoiceNumber: payment.invoiceNumber,
        collaboratorId: payment.collaboratorId,
        collaboratorName: payment.collaboratorName,
        paymentDate: payment.paymentDate.toISOString().split('T')[0],
        amount: payment.amount,
        currency: payment.currency,
        paymentMethod: payment.paymentMethod,
        proofUrl: payment.proofUrl || '',
        proofName: payment.proofName || '',
        status: payment.status,
        notes: payment.notes || ''
      });
    } else {
      setFormData({
        invoiceId: '',
        invoiceNumber: '',
        collaboratorId: '',
        collaboratorName: '',
        paymentDate: new Date().toISOString().split('T')[0],
        amount: 0,
        currency: 'EUR',
        paymentMethod: '',
        proofUrl: '',
        proofName: '',
        status: 'scheduled',
        notes: ''
      });
    }
  }, [payment]);

  const loadInvoicesData = async () => {
    try {
      setLoadingData(true);
      console.log('🔄 Cargando facturas validadas para pagos...');
      
      // Load validated invoices that haven't been paid yet
      const allInvoices = await collaboratorInvoicesService.getAll();
      const validatedInvoices = allInvoices.filter(invoice => 
        invoice.status === 'validated' || invoice.status === 'received'
      );
      
      const invoicesForPayment = validatedInvoices.map(invoice => ({
        id: invoice.id,
        number: invoice.invoiceNumber,
        collaboratorId: invoice.collaboratorId,
        collaboratorName: invoice.collaboratorName,
        amount: invoice.amount,
        currency: invoice.currency
      }));
      
      setInvoices(invoicesForPayment);
      console.log('✅ Facturas para pago cargadas:', invoicesForPayment.length);
    } catch (error) {
      console.error('Error loading invoices data:', error);
      alert('Error al cargar las facturas para pago');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInvoiceChange = (invoiceId: string) => {
    const selectedInvoice = invoices.find(i => i.id === invoiceId);
    if (selectedInvoice) {
      setFormData({
        ...formData,
        invoiceId,
        invoiceNumber: selectedInvoice.number,
        collaboratorId: selectedInvoice.collaboratorId,
        collaboratorName: selectedInvoice.collaboratorName,
        amount: selectedInvoice.amount,
        currency: selectedInvoice.currency
      });
    }
  };

  const [uploadingProof, setUploadingProof] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingProof(true);
      const { supabase } = await import('../../services/supabaseService');
      // path convención: {collaboratorId}/{timestamp}_{filename} — RLS del
      // bucket lo usa para permitir al colaborador ver los suyos.
      const collabId = formData.collaboratorId || 'unknown';
      const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
      const path = `${collabId}/${Date.now()}_${safeName}`;

      const { error: upErr } = await supabase.storage
        .from('payment-proofs')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;

      setFormData({
        ...formData,
        proofUrl: path,             // storage_path, no URL pública
        proofName: file.name,
      });
    } catch (err: any) {
      alert('Error subiendo justificante: ' + (err?.message || err));
    } finally {
      setUploadingProof(false);
      event.target.value = '';
    }
  };

  const openProof = async () => {
    if (!formData.proofUrl) return;
    const { supabase } = await import('../../services/supabaseService');
    const { data } = await supabase.storage
      .from('payment-proofs')
      .createSignedUrl(formData.proofUrl, 60 * 60);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      paymentDate: new Date(formData.paymentDate)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {payment ? 'Editar Pago' : 'Registrar Nuevo Pago'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Invoice Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Factura Asociada</span>
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seleccionar factura *
              </label>
              <select
                required
                value={formData.invoiceId}
                onChange={(e) => handleInvoiceChange(e.target.value)}
                disabled={loadingData}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">{loadingData ? 'Cargando facturas...' : 'Seleccionar factura'}</option>
                {invoices.map(invoice => (
                  <option key={invoice.id} value={invoice.id}>
                    {invoice.number} - {invoice.collaboratorName} ({formatCurrency(invoice.amount, invoice.currency)})
                  </option>
                ))}
              </select>
              {!loadingData && invoices.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No hay facturas disponibles. Registra facturas en la pestaña "Facturas".
                </p>
              )}
            </div>

            {formData.collaboratorName && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">Información de la factura</span>
                </div>
                <div className="text-sm text-blue-700">
                  <p><strong>Colaborador:</strong> {formData.collaboratorName}</p>
                  <p><strong>Número:</strong> {formData.invoiceNumber}</p>
                  <p><strong>Importe:</strong> {formatCurrency(formData.amount, formData.currency)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Payment Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <CreditCard className="w-5 h-5" />
              <span>Detalles del Pago</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha de pago *
                </label>
                <input
                  type="date"
                  required
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Método de pago *
                </label>
                <select
                  required
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar método</option>
                  {paymentMethods.map(method => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Importe *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500 text-sm">
                    {currencySymbol(formData.currency)}
                  </span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="1800.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Payment['status'] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="scheduled">Programado</option>
                  <option value="executed">Ejecutado</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment Proof */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
              <Upload className="w-5 h-5" />
              <span>Comprobante de Pago</span>
            </h3>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              {formData.proofName ? (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={openProof}
                    className="flex items-center space-x-2 text-left"
                    title="Ver justificante"
                  >
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600 hover:underline">
                      {formData.proofName}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, proofUrl: '', proofName: '' })}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    {uploadingProof
                      ? 'Subiendo justificante…'
                      : 'Arrastra el justificante aquí o haz clic para seleccionar'}
                  </p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    disabled={uploadingProof}
                    className="hidden"
                    id="proof-file"
                  />
                  <label
                    htmlFor="proof-file"
                    className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium bg-white cursor-pointer ${
                      uploadingProof
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {uploadingProof ? 'Subiendo…' : 'Seleccionar archivo'}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">PDF, JPG, PNG</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas adicionales
            </label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Información adicional sobre el pago..."
            />
          </div>

          {/* Approval Status Information */}
          {payment && (
            <div className={`border rounded-lg p-4 ${
              payment.approvalStatus === 'approved' ? 'bg-green-50 border-green-200' :
              payment.approvalStatus === 'rejected' ? 'bg-red-50 border-red-200' :
              'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-start space-x-3">
                <CreditCard className={`w-5 h-5 mt-0.5 ${
                  payment.approvalStatus === 'approved' ? 'text-green-600' :
                  payment.approvalStatus === 'rejected' ? 'text-red-600' :
                  'text-orange-600'
                }`} />
                <div className="flex-1">
                  <h4 className={`text-sm font-medium ${
                    payment.approvalStatus === 'approved' ? 'text-green-900' :
                    payment.approvalStatus === 'rejected' ? 'text-red-900' :
                    'text-orange-900'
                  }`}>
                    Estado de Aprobación: {
                      payment.approvalStatus === 'approved' ? 'Aprobado' :
                      payment.approvalStatus === 'rejected' ? 'Rechazado' :
                      'Pendiente de Aprobación'
                    }
                  </h4>
                  <p className={`text-sm mt-1 ${
                    payment.approvalStatus === 'approved' ? 'text-green-700' :
                    payment.approvalStatus === 'rejected' ? 'text-red-700' :
                    'text-orange-700'
                  }`}>
                    {payment.approvalStatus === 'approved' &&
                      `Aprobado por ${payment.approvedBy || 'Usuario'} ${payment.approvedAt ? `el ${payment.approvedAt.toLocaleDateString()}` : ''}`
                    }
                    {payment.approvalStatus === 'rejected' &&
                      `Rechazado por ${payment.approvedBy || 'Usuario'} ${payment.approvedAt ? `el ${payment.approvedAt.toLocaleDateString()}` : ''}`
                    }
                    {payment.approvalStatus === 'rejected' && payment.rejectionReason && (
                      <span className="block mt-1 font-medium">Razón: {payment.rejectionReason}</span>
                    )}
                    {payment.approvalStatus === 'pending' &&
                      'Este pago está pendiente de aprobación. Una vez aprobado, podrá ser ejecutado.'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Information Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <CreditCard className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-900">Información importante</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  {payment ?
                    'Los cambios realizados no afectarán el estado de aprobación. Los pagos ejecutados no pueden ser modificados.' :
                    'Los pagos nuevos requieren aprobación antes de poder ser ejecutados. Una vez ejecutado, se actualizará automáticamente el estado de la factura asociada.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {payment ? 'Actualizar' : 'Registrar'} Pago
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;